import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ImageUploader from './components/ImageUploader';
import MyListedProducts from './components/MyListedProducts';
import type { CompressedImage } from './utils/imageCompress';
import { supabase } from '@/lib/supabase';

interface ColorVariant {
  id: string;
  name: string;
  hex: string;
  price: string;
  images: CompressedImage[];
}

// stock[colorName][size] = quantity
type StockMatrix = Record<string, Record<string, string>>;

interface ProductForm {
  name: string;
  category: string;
  price: string;
  originalPrice: string;
  description: string;
  sizes: string[];
  brand: string;
  tags: string;
}

const CATEGORIES = [
  'T-Shirts', 'Shirts', 'Jeans', 'Track Pants', 'Kurta', 'Saree',
  'Dress', 'Jacket', 'Hoodies', 'Shorts', 'Leggings', 'Ethnic Wear', 'Other',
];

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'Free Size'];

const INITIAL_FORM: ProductForm = {
  name: '', category: '', price: '', originalPrice: '',
  description: '', sizes: [], brand: '', tags: '',
};

const COLOR_PALETTE = [
  { name: 'Black',    hex: '#1a1a1a' },
  { name: 'White',    hex: '#f5f5f5' },
  { name: 'Red',      hex: '#e53e3e' },
  { name: 'Navy',     hex: '#1a365d' },
  { name: 'Sky Blue', hex: '#63b3ed' },
  { name: 'Green',    hex: '#38a169' },
  { name: 'Yellow',   hex: '#ecc94b' },
  { name: 'Orange',   hex: '#ed8936' },
  { name: 'Pink',     hex: '#ed64a6' },
  { name: 'Maroon',   hex: '#742a2a' },
  { name: 'Grey',     hex: '#718096' },
  { name: 'Beige',    hex: '#d4a574' },
  { name: 'Brown',    hex: '#7b4f2e' },
  { name: 'Olive',    hex: '#6b7c2e' },
  { name: 'Teal',     hex: '#2c7a7b' },
  { name: 'Lavender', hex: '#b794f4' },
];

const STOCK_PRESETS = [5, 10, 25, 50, 100];

// ── Stock Matrix Helpers ──
function getStockValue(matrix: StockMatrix, color: string, size: string): string {
  return matrix[color]?.[size] ?? '';
}

function setStockValue(matrix: StockMatrix, color: string, size: string, value: string): StockMatrix {
  return {
    ...matrix,
    [color]: {
      ...(matrix[color] ?? {}),
      [size]: value,
    },
  };
}

function fillColorStock(matrix: StockMatrix, color: string, sizes: string[], value: string): StockMatrix {
  const updated = { ...matrix, [color]: { ...(matrix[color] ?? {}) } };
  sizes.forEach(s => { updated[color][s] = value; });
  return updated;
}

function fillSizeStock(matrix: StockMatrix, size: string, colors: string[], value: string): StockMatrix {
  const updated = { ...matrix };
  colors.forEach(c => {
    updated[c] = { ...(updated[c] ?? {}), [size]: value };
  });
  return updated;
}

function totalStock(matrix: StockMatrix): number {
  return Object.values(matrix).reduce((sum, sizeMap) =>
    sum + Object.values(sizeMap).reduce((s, v) => s + (Number(v) || 0), 0), 0);
}

// ── No-color stock (only sizes, no color variants) ──
type SizeStockMap = Record<string, string>;

// ── Upload video to Supabase Storage ──
async function uploadVideoToStorage(file: File, folder: string): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'mp4';
  const fileName = `${folder}/video-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('product-videos')
    .upload(fileName, file, { contentType: file.type, upsert: false });
  if (error) throw new Error(`Video upload failed: ${error.message}`);
  const { data: urlData } = supabase.storage.from('product-videos').getPublicUrl(fileName);
  return urlData.publicUrl;
}

// ── Upload image to Supabase Storage ──
async function uploadImageToStorage(dataUrl: string, folder: string): Promise<string> {
  // Convert base64 dataUrl to Blob
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const ext = blob.type.split('/')[1] || 'jpg';
  const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('product-images')
    .upload(fileName, blob, { contentType: blob.type, upsert: false });

  if (error) throw new Error(`Image upload failed: ${error.message}`);

  const { data: urlData } = supabase.storage
    .from('product-images')
    .getPublicUrl(fileName);

  return urlData.publicUrl;
}

async function uploadImages(images: CompressedImage[], folder: string): Promise<string[]> {
  const urls: string[] = [];
  for (const img of images) {
    try {
      const url = await uploadImageToStorage(img.dataUrl, folder);
      urls.push(url);
    } catch {
      // skip failed images
    }
  }
  return urls;
}

export default function ProductListingPage() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState<'list' | 'my-products'>('list');
  const [form, setForm] = useState<ProductForm>(INITIAL_FORM);
  const [mainImages, setMainImages] = useState<CompressedImage[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [colorVariants, setColorVariants] = useState<ColorVariant[]>([]);
  const [activeColorId, setActiveColorId] = useState<string | null>(null);
  const [customColorName, setCustomColorName] = useState('');
  const [customColorHex, setCustomColorHex] = useState('#888888');
  const [errors, setErrors] = useState<Partial<Record<keyof ProductForm | 'images' | 'stock', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState<'images' | 'details' | 'pricing' | 'variants' | 'stock'>('images');

  // Stock state
  // If colors exist: stockMatrix[colorName][size]
  // If no colors: sizeStock[size]
  const [stockMatrix, setStockMatrix] = useState<StockMatrix>({});
  const [sizeStock, setSizeStock] = useState<SizeStockMap>({});
  const [fillAllValue, setFillAllValue] = useState('');

  const update = (field: keyof ProductForm, value: string | string[]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleSize = (size: string) => {
    const updated = form.sizes.includes(size)
      ? form.sizes.filter(s => s !== size)
      : [...form.sizes, size];
    update('sizes', updated);
  };

  // ── Colour Variant Helpers ──
  const addColorVariant = (name: string, hex: string) => {
    if (colorVariants.find(c => c.name.toLowerCase() === name.toLowerCase())) return;
    const newVariant: ColorVariant = {
      id: `cv-${Date.now()}-${Math.random()}`,
      name,
      hex,
      price: form.price,
      images: [],
    };
    setColorVariants(prev => [...prev, newVariant]);
    setActiveColorId(newVariant.id);
  };

  const addCustomColor = () => {
    const trimmed = customColorName.trim();
    if (!trimmed) return;
    addColorVariant(trimmed, customColorHex);
    setCustomColorName('');
    setCustomColorHex('#888888');
  };

  const removeColorVariant = (id: string) => {
    const cv = colorVariants.find(c => c.id === id);
    if (cv) {
      setStockMatrix(prev => {
        const updated = { ...prev };
        delete updated[cv.name];
        return updated;
      });
    }
    setColorVariants(prev => prev.filter(c => c.id !== id));
    if (activeColorId === id) setActiveColorId(null);
  };

  const updateVariantPrice = (id: string, price: string) => {
    setColorVariants(prev => prev.map(c => c.id === id ? { ...c, price } : c));
  };

  const updateVariantImages = (id: string, images: CompressedImage[]) => {
    setColorVariants(prev => prev.map(c => c.id === id ? { ...c, images } : c));
  };

  // ── Stock total calculation ──
  const hasColors = colorVariants.length > 0;
  const hasSizes = form.sizes.length > 0;

  const computedTotalStock = (): number => {
    if (hasColors && hasSizes) return totalStock(stockMatrix);
    if (hasColors && !hasSizes) {
      return colorVariants.reduce((sum, cv) => {
        const val = stockMatrix[cv.name]?.['__nosize__'];
        return sum + (Number(val) || 0);
      }, 0);
    }
    if (!hasColors && hasSizes) {
      return form.sizes.reduce((sum, s) => sum + (Number(sizeStock[s]) || 0), 0);
    }
    // no colors, no sizes — single stock
    return Number(sizeStock['__nosize__'] ?? 0);
  };

  const stockTotal = computedTotalStock();
  const stockStatus = stockTotal === 0 ? 'out' : stockTotal <= 5 ? 'low' : stockTotal <= 20 ? 'medium' : 'good';

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!form.name.trim()) newErrors.name = 'Product name is required';
    if (!form.category) newErrors.category = 'Select a category';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) newErrors.price = 'Enter valid price';
    if (computedTotalStock() < 1) newErrors.stock = 'Kam se kam 1 unit stock add karo';
    if (!form.description.trim()) newErrors.description = 'Add a description';
    if (mainImages.length === 0 && colorVariants.every(c => c.images.length === 0)) {
      newErrors.images = 'Kam se kam 1 product photo add karo (video optional hai)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      if (errors.images) setActiveSection('images');
      else if (errors.name || errors.category || errors.description || errors.brand) setActiveSection('details');
      else if (errors.price) setActiveSection('pricing');
      else if (errors.stock) setActiveSection('stock');
      return;
    }

    setSubmitting(true);

    try {
      const productFolder = `prod-${Date.now()}`;

      // Upload main images to storage
      const uploadedMainImages = await uploadImages(mainImages, productFolder);

      // Upload color variant images to storage
      const colorsData = await Promise.all(colorVariants.map(async (cv) => {
        const uploadedColorImages = await uploadImages(cv.images, `${productFolder}/${cv.name}`);
        return {
          name: cv.name,
          hex: cv.hex,
          price: cv.price ? Number(cv.price) : Number(form.price),
          image: uploadedColorImages[0] ?? '',
          images: uploadedColorImages,
        };
      }));

      // Thumbnail
      const thumbnail =
        colorsData[0]?.images[0] ||
        uploadedMainImages[0] ||
        '';

      // Upload video if present (completely optional — skip silently if not provided or fails)
      let uploadedVideoUrl: string | null = null;
      if (videoFile && videoFile.size > 0) {
        try {
          uploadedVideoUrl = await uploadVideoToStorage(videoFile, productFolder);
        } catch {
          // Video upload failed — continue without video, no error shown to user
          uploadedVideoUrl = null;
        }
      }

      // Build stock breakdown
      let stockBreakdown: Record<string, unknown> = {};
      if (hasColors && hasSizes) {
        stockBreakdown = stockMatrix;
      } else if (hasColors && !hasSizes) {
        colorVariants.forEach(cv => {
          stockBreakdown[cv.name] = Number(stockMatrix[cv.name]?.['__nosize__'] ?? 0);
        });
      } else if (!hasColors && hasSizes) {
        form.sizes.forEach(s => {
          stockBreakdown[s] = Number(sizeStock[s] ?? 0);
        });
      } else {
        stockBreakdown['total'] = Number(sizeStock['__nosize__'] ?? 0);
      }

      // Get seller info from localStorage (if logged in)
      let listedByPhone: string | null = null;
      let listedByEmail: string | null = null;
      try {
        const auth = localStorage.getItem('clothybazar_customer_auth');
        if (auth) {
          const user = JSON.parse(auth);
          listedByPhone = user?.phone || null;
          listedByEmail = user?.email || null;
        }
      } catch { /* ignore */ }

      // Save to Supabase with storage URLs (not base64)
      const { error } = await supabase.from('products').insert([{
        name: form.name,
        category: form.category,
        price: Number(form.price),
        original_price: Number(form.originalPrice) || Number(form.price),
        description: form.description,
        sizes: form.sizes,
        colors: colorsData,
        stock: stockTotal,
        stock_breakdown: stockBreakdown,
        brand: form.brand,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        thumbnail,
        images: uploadedMainImages,
        video: uploadedVideoUrl,
        video_thumbnail: uploadedVideoUrl ? (thumbnail || null) : null,
        status: 'pending',
        listed_by_phone: listedByPhone,
        listed_by_email: listedByEmail,
      }]);

      if (error) {
        console.error('Product save error:', error);
        setErrors({ ...errors, stock: 'Product save nahi hua. Dobara try karo.' });
        setSubmitting(false);
        return;
      }

      // Save only lightweight metadata to localStorage
      try {
        const productMeta = {
          id: `PROD-${Date.now()}`,
          name: form.name,
          category: form.category,
          price: Number(form.price),
          originalPrice: Number(form.originalPrice) || Number(form.price),
          sizes: form.sizes,
          brand: form.brand,
          stock: stockTotal,
          createdAt: new Date().toISOString(),
          outOfStock: stockTotal === 0,
          status: 'pending',
        };
        const existing = JSON.parse(localStorage.getItem('listed_products_meta') ?? '[]');
        const trimmed = existing.slice(0, 19);
        localStorage.setItem('listed_products_meta', JSON.stringify([productMeta, ...trimmed]));
      } catch {
        // Ignore localStorage errors
      }

      setSubmitting(false);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Submit error:', err);
      setErrors({ ...errors, stock: 'Kuch galat hua. Internet check karo aur dobara try karo.' });
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setMainImages([]);
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoFile(null);
    setVideoPreviewUrl('');
    setColorVariants([]);
    setActiveColorId(null);
    setErrors({});
    setSuccess(false);
    setActiveSection('images');
    setStockMatrix({});
    setSizeStock({});
    setFillAllValue('');
  };

  const SECTIONS = [
    { key: 'images',   label: 'Photos',   icon: 'ri-image-2-line',     done: mainImages.length > 0 || colorVariants.some(c => c.images.length > 0) },
    { key: 'details',  label: 'Details',  icon: 'ri-file-text-line',   done: !!(form.name && form.category && form.description) },
    { key: 'pricing',  label: 'Pricing',  icon: 'ri-price-tag-3-line', done: !!(form.price) },
    { key: 'variants', label: 'Variants', icon: 'ri-palette-line',     done: form.sizes.length > 0 || colorVariants.length > 0 },
    { key: 'stock',    label: 'Stock',    icon: 'ri-stack-line',       done: stockTotal > 0 },
  ] as const;

  const discount = form.originalPrice && form.price && Number(form.originalPrice) > Number(form.price)
    ? Math.round((1 - Number(form.price) / Number(form.originalPrice)) * 100)
    : 0;

  const activeVariant = colorVariants.find(c => c.id === activeColorId) ?? null;

  // ── SUCCESS SCREEN ──
  if (success) {
    const thumbSrc = (colorVariants[0]?.images[0]?.dataUrl) || mainImages[0]?.dataUrl;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center border border-gray-200">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Product Listed!</h2>
          <p className="text-gray-400 text-xs mb-3">Pending admin approval — will go live soon</p>
          {thumbSrc && (
            <img src={thumbSrc} alt={form.name} className="w-28 h-28 object-cover rounded-xl mx-auto mb-4 border border-gray-200" />
          )}
          <div className="bg-gray-50 rounded-xl p-3 mb-5 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Product</span>
              <span className="font-bold text-gray-900 truncate max-w-[140px]">{form.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Base Price</span>
              <span className="font-bold text-gray-900">₹{form.price}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Category</span>
              <span className="font-medium text-gray-700">{form.category}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total Stock</span>
              <span className={`font-bold ${stockTotal > 10 ? 'text-green-600' : 'text-amber-600'}`}>{stockTotal} units</span>
            </div>
            {videoFile && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Video</span>
                <span className="font-semibold text-purple-600 flex items-center gap-1">
                  <i className="ri-video-fill text-xs"></i>5-sec video added
                </span>
              </div>
            )}
            {colorVariants.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Colours</span>
                <span className="font-medium text-gray-700 flex items-center gap-1">
                  {colorVariants.map(cv => (
                    <span key={cv.id} className="w-4 h-4 rounded-full border border-gray-300 inline-block" style={{ backgroundColor: cv.hex }} title={cv.name}></span>
                  ))}
                  <span className="ml-1">{colorVariants.length} colours</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line mr-1"></i>List Another
            </button>
            <Link to="/" className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors text-center whitespace-nowrap">
              <i className="ri-home-line mr-1"></i>Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900 leading-none">
                {mainTab === 'list' ? 'List a Product' : 'My Products'}
              </h1>
              <p className="text-gray-400 text-xs">
                {mainTab === 'list' ? 'Add your product to the store' : 'Images update karo'}
              </p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <i className="ri-store-2-line"></i>
            <span className="hidden sm:inline">Store</span>
          </Link>
        </div>
        {/* Main Tab Switcher */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setMainTab('list')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                mainTab === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-add-circle-line text-sm"></i>
              New Listing
            </button>
            <button
              onClick={() => setMainTab('my-products')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                mainTab === 'my-products' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-image-edit-line text-sm"></i>
              My Products
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5 pb-28">
        {/* My Products Tab */}
        {mainTab === 'my-products' && <MyListedProducts />}

        {/* New Listing Tab */}
        {mainTab === 'list' && (<>

        {/* Progress Steps */}
        <div className="flex gap-1.5 mb-5">
          {SECTIONS.map((sec) => (
            <button
              key={sec.key}
              onClick={() => setActiveSection(sec.key)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeSection === sec.key
                  ? 'bg-gray-900 text-white'
                  : sec.done
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="relative">
                <i className={`${sec.icon} text-base`}></i>
                {sec.done && activeSection !== sec.key && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white" style={{ fontSize: '7px' }}></i>
                  </span>
                )}
              </div>
              <span className="leading-none">{sec.label}</span>
            </button>
          ))}
        </div>

        {/* ── SECTION: IMAGES ── */}
        {activeSection === 'images' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <i className="ri-image-2-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Main Product Images</p>
                  <p className="text-gray-400 text-xs">General product photos (all colours)</p>
                </div>
              </div>
              <ImageUploader
                images={mainImages}
                onChange={setMainImages}
                videoFile={videoFile}
                videoPreviewUrl={videoPreviewUrl}
                onVideoChange={(file, previewUrl) => {
                  setVideoFile(file);
                  setVideoPreviewUrl(previewUrl);
                }}
              />
              {errors.images && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{errors.images}
                </p>
              )}
            </div>

            {colorVariants.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <i className="ri-palette-line text-orange-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm">Colour Variant Images</p>
                    <p className="text-gray-400 text-xs">Har colour ke liye alag photos add karo</p>
                  </div>
                </div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {colorVariants.map(cv => (
                    <button
                      key={cv.id}
                      onClick={() => setActiveColorId(cv.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        activeColorId === cv.id
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full border border-white/50 shrink-0" style={{ backgroundColor: cv.hex }}></span>
                      {cv.name}
                      {cv.images.length > 0 && (
                        <span className={`text-[9px] font-bold px-1 rounded-full ${activeColorId === cv.id ? 'bg-white/20 text-white' : 'bg-green-100 text-green-600'}`}>
                          {cv.images.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {activeVariant && (
                  <div className="border border-dashed border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" style={{ backgroundColor: activeVariant.hex }}></span>
                      <p className="text-gray-700 font-semibold text-sm">{activeVariant.name} — Photos</p>
                    </div>
                    <ImageUploader
                      images={activeVariant.images}
                      onChange={(imgs) => updateVariantImages(activeVariant.id, imgs)}
                    />
                  </div>
                )}
                {!activeColorId && (
                  <p className="text-gray-400 text-xs text-center py-4">Upar colour select karo uski photos add karne ke liye</p>
                )}
              </div>
            )}

            <button
              onClick={() => setActiveSection('details')}
              className="w-full py-3 rounded-xl bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Next: Product Details <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        )}

        {/* ── SECTION: DETAILS ── */}
        {activeSection === 'details' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-gray-900 font-bold text-base flex items-center gap-2">
              <i className="ri-file-text-line text-gray-500"></i>Product Details
            </h2>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="e.g. Men's Slim Fit Cotton T-Shirt"
                maxLength={100}
                className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all ${
                  errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100'
                }`}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.name}</p>}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1.5">Brand Name</label>
              <input
                type="text"
                value={form.brand}
                onChange={e => update('brand', e.target.value)}
                placeholder="e.g. Levi's, H&M, Local Brand"
                maxLength={50}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => update('category', cat)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      form.category === cat
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {errors.category && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.category}</p>}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.description}
                onChange={e => update('description', e.target.value)}
                placeholder="Describe your product — material, fit, features, care instructions..."
                rows={4}
                maxLength={500}
                className={`w-full px-4 py-3 rounded-xl border text-sm outline-none resize-none transition-all ${
                  errors.description ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100'
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.description
                  ? <p className="text-red-500 text-xs flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.description}</p>
                  : <span />
                }
                <span className="text-gray-400 text-xs">{form.description.length}/500</span>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1.5">Tags</label>
              <input
                type="text"
                value={form.tags}
                onChange={e => update('tags', e.target.value)}
                placeholder="casual, summer, cotton (comma separated)"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setActiveSection('images')} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
              <button onClick={() => setActiveSection('pricing')} className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Next: Pricing <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── SECTION: PRICING ── */}
        {activeSection === 'pricing' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-gray-900 font-bold text-base flex items-center gap-2">
              <i className="ri-price-tag-3-line text-gray-500"></i>Pricing
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Base Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={e => update('price', e.target.value)}
                    placeholder="499"
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border text-sm outline-none transition-all ${
                      errors.price ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100'
                    }`}
                  />
                </div>
                {errors.price && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.price}</p>}
                <p className="text-gray-400 text-[10px] mt-1">Har colour ka alag price Variants mein set karo</p>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Original Price
                  <span className="text-gray-400 font-normal text-xs ml-1">(MRP)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min="0"
                    value={form.originalPrice}
                    onChange={e => update('originalPrice', e.target.value)}
                    placeholder="999"
                    className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all"
                  />
                </div>
              </div>
            </div>

            {discount > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                  <i className="ri-percent-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-green-700 font-bold text-sm">{discount}% Discount</p>
                  <p className="text-green-600 text-xs">Customer saves ₹{Number(form.originalPrice) - Number(form.price)}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={() => setActiveSection('details')} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
              <button onClick={() => setActiveSection('variants')} className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Next: Variants <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── SECTION: VARIANTS ── */}
        {activeSection === 'variants' && (
          <div className="space-y-4">
            {/* Sizes */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h2 className="text-gray-900 font-bold text-base flex items-center gap-2 mb-4">
                <i className="ri-t-shirt-line text-gray-500"></i>Available Sizes
              </h2>
              <div className="flex flex-wrap gap-2">
                {SIZE_OPTIONS.map(size => (
                  <button
                    key={size}
                    onClick={() => toggleSize(size)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all cursor-pointer whitespace-nowrap ${
                      form.sizes.includes(size)
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {form.sizes.length > 0 && (
                <p className="text-gray-500 text-xs mt-2">
                  Selected: <span className="font-semibold text-gray-700">{form.sizes.join(', ')}</span>
                </p>
              )}
            </div>

            {/* Colour Variants */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-gray-900 font-bold text-base flex items-center gap-2">
                  <i className="ri-palette-line text-gray-500"></i>Colour Variants
                </h2>
                {colorVariants.length > 0 && (
                  <span className="text-xs font-bold bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full border border-orange-200">
                    {colorVariants.length} colours
                  </span>
                )}
              </div>

              <p className="text-gray-500 text-xs mb-4 flex items-start gap-1.5">
                <i className="ri-information-line text-gray-400 mt-0.5 shrink-0"></i>
                Har colour ke liye alag image aur price set kar sakte ho. Stock alag tab mein set hoga.
              </p>

              {/* Palette */}
              <div className="grid grid-cols-8 gap-2 mb-4">
                {COLOR_PALETTE.map(color => {
                  const isAdded = colorVariants.some(c => c.name === color.name);
                  return (
                    <button
                      key={color.name}
                      onClick={() => isAdded ? removeColorVariant(colorVariants.find(c => c.name === color.name)!.id) : addColorVariant(color.name, color.hex)}
                      title={color.name}
                      className="relative flex flex-col items-center gap-1 cursor-pointer"
                    >
                      <div
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          isAdded ? 'border-orange-500 scale-110' : 'border-gray-200 hover:border-gray-400 hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.hex }}
                      >
                        {isAdded && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <i className="ri-check-line text-[10px] font-bold" style={{ color: ['#f5f5f5', '#ecc94b', '#d4a574'].includes(color.hex) ? '#333' : '#fff' }}></i>
                          </div>
                        )}
                      </div>
                      <span className="text-[8px] text-gray-500 leading-none text-center truncate w-full">{color.name}</span>
                    </button>
                  );
                })}
              </div>

              {/* Custom Colour / Print Variant */}
              <div className="border border-dashed border-orange-200 rounded-xl p-3 mb-4 bg-orange-50/30">
                <p className="text-gray-700 text-xs font-bold mb-1 flex items-center gap-1">
                  <i className="ri-add-circle-line text-orange-500"></i>
                  Custom Colour / Print Variant Add Karo
                </p>
                <p className="text-gray-400 text-[10px] mb-3 leading-relaxed">
                  Same colour ke alag prints ke liye naam mein print describe karo.<br />
                  <span className="text-orange-500 font-semibold">Example: &quot;Black - Floral Print&quot;, &quot;Black - Striped&quot;, &quot;Red - Solid&quot;</span>
                </p>

                {/* Quick Print Suggestions */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {['Floral Print', 'Striped', 'Solid', 'Checked', 'Abstract', 'Geometric', 'Animal Print', 'Tie-Dye'].map(print => (
                    <button
                      key={print}
                      onClick={() => setCustomColorName(prev => {
                        if (prev.includes(' - ')) {
                          return prev.split(' - ')[0] + ' - ' + print;
                        }
                        return prev ? prev + ' - ' + print : print;
                      })}
                      className="px-2 py-1 rounded-full text-[10px] font-semibold border border-orange-200 bg-white text-orange-600 hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap"
                    >
                      {print}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="color"
                      value={customColorHex}
                      onChange={e => setCustomColorHex(e.target.value)}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 shrink-0"
                      title="Pick colour"
                    />
                    <input
                      type="text"
                      value={customColorName}
                      onChange={e => setCustomColorName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomColor()}
                      placeholder="e.g. Black - Floral Print"
                      maxLength={50}
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-orange-400 transition-all"
                    />
                  </div>
                  <button
                    onClick={addCustomColor}
                    disabled={!customColorName.trim()}
                    className="px-4 py-2 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>

                {/* Live preview */}
                {customColorName.trim() && (
                  <div className="mt-2 flex items-center gap-2 p-2 bg-white rounded-lg border border-orange-100">
                    <span className="w-5 h-5 rounded-full border border-gray-200 shrink-0" style={{ backgroundColor: customColorHex }}></span>
                    <span className="text-gray-700 text-xs font-semibold">{customColorName}</span>
                    <span className="text-gray-400 text-[10px] ml-auto">Preview</span>
                  </div>
                )}
              </div>

              {/* Added Colour Variants — price per colour */}
              {colorVariants.length > 0 && (
                <div className="space-y-3">
                  <p className="text-gray-700 text-xs font-bold uppercase tracking-wide">Added Colours — Price Set Karo</p>
                  {colorVariants.map(cv => (
                    <div key={cv.id} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full border-2 border-gray-200 shrink-0" style={{ backgroundColor: cv.hex }}></div>
                        <div className="flex-1">
                          <p className="text-gray-800 font-semibold text-sm">{cv.name}</p>
                          {cv.images.length > 0 ? (
                            <p className="text-green-600 text-xs flex items-center gap-1">
                              <i className="ri-image-line"></i>{cv.images.length} photo{cv.images.length > 1 ? 's' : ''} added
                            </p>
                          ) : (
                            <button
                              onClick={() => { setActiveColorId(cv.id); setActiveSection('images'); }}
                              className="text-orange-500 text-xs flex items-center gap-1 cursor-pointer hover:underline"
                            >
                              <i className="ri-image-add-line"></i>Photos add karo (Photos tab)
                            </button>
                          )}
                        </div>
                        {cv.images[0] && (
                          <img src={cv.images[0].dataUrl} alt={cv.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0" />
                        )}
                        <button
                          onClick={() => removeColorVariant(cv.id)}
                          className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-red-400 hover:bg-red-100 transition-colors cursor-pointer shrink-0"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-gray-500 text-xs whitespace-nowrap">Price for {cv.name}:</label>
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-bold">₹</span>
                          <input
                            type="number"
                            min="0"
                            value={cv.price}
                            onChange={e => updateVariantPrice(cv.id, e.target.value)}
                            placeholder={form.price || '499'}
                            className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 transition-all"
                          />
                        </div>
                        {cv.price && cv.price !== form.price && form.price && (
                          <span className={`text-xs font-semibold whitespace-nowrap ${Number(cv.price) > Number(form.price) ? 'text-red-500' : 'text-green-600'}`}>
                            {Number(cv.price) > Number(form.price) ? `+₹${Number(cv.price) - Number(form.price)}` : `-₹${Number(form.price) - Number(cv.price)}`}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setActiveSection('pricing')} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
              <button onClick={() => setActiveSection('stock')} className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Next: Stock <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── SECTION: STOCK ── */}
        {activeSection === 'stock' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-gray-900 font-bold text-base flex items-center gap-2">
                  <i className="ri-stack-line text-gray-500"></i>Stock Management
                </h2>
                {stockTotal > 0 && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    stockStatus === 'out' ? 'bg-red-100 text-red-600' :
                    stockStatus === 'low' ? 'bg-amber-100 text-amber-700' :
                    stockStatus === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    Total: {stockTotal} units
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs mb-5">
                {hasColors && hasSizes
                  ? 'Har colour aur size ke liye alag stock bharo'
                  : hasColors
                  ? 'Har colour ke liye stock bharo'
                  : hasSizes
                  ? 'Har size ke liye stock bharo'
                  : 'Product ka total stock bharo'}
              </p>

              {/* ── CASE 1: Colors + Sizes → Matrix ── */}
              {hasColors && hasSizes && (
                <div>
                  {/* Quick fill row */}
                  <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <i className="ri-flashlight-line text-gray-400 text-sm shrink-0"></i>
                    <span className="text-gray-600 text-xs font-semibold whitespace-nowrap">Sabko ek saath:</span>
                    <input
                      type="number"
                      min="0"
                      value={fillAllValue}
                      onChange={e => setFillAllValue(e.target.value)}
                      placeholder="qty"
                      className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center"
                    />
                    <button
                      onClick={() => {
                        if (!fillAllValue) return;
                        let updated = { ...stockMatrix };
                        colorVariants.forEach(cv => {
                          form.sizes.forEach(s => {
                            updated = setStockValue(updated, cv.name, s, fillAllValue);
                          });
                        });
                        setStockMatrix(updated);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-colors"
                    >
                      Fill All
                    </button>
                  </div>

                  {/* Matrix table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="text-left text-gray-500 font-semibold pb-2 pr-3 whitespace-nowrap">Colour / Size</th>
                          {form.sizes.map(size => (
                            <th key={size} className="text-center text-gray-700 font-bold pb-2 px-1 min-w-[56px]">
                              <div className="flex flex-col items-center gap-1">
                                <span>{size}</span>
                                {/* Fill column button */}
                                <button
                                  onClick={() => {
                                    const val = prompt(`${size} ke liye sabka stock:`);
                                    if (val && !isNaN(Number(val))) {
                                      setStockMatrix(prev => fillSizeStock(prev, size, colorVariants.map(c => c.name), val));
                                    }
                                  }}
                                  className="text-[9px] text-orange-500 hover:underline cursor-pointer whitespace-nowrap"
                                  title={`Fill all ${size}`}
                                >
                                  fill
                                </button>
                              </div>
                            </th>
                          ))}
                          <th className="text-center text-gray-500 font-semibold pb-2 px-1 whitespace-nowrap">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {colorVariants.map(cv => {
                          const rowTotal = form.sizes.reduce((sum, s) => sum + (Number(getStockValue(stockMatrix, cv.name, s)) || 0), 0);
                          return (
                            <tr key={cv.id} className="border-t border-gray-100">
                              <td className="py-2 pr-3">
                                <div className="flex items-center gap-1.5 whitespace-nowrap">
                                  <span className="w-4 h-4 rounded-full border border-gray-200 shrink-0" style={{ backgroundColor: cv.hex }}></span>
                                  <span className="text-gray-700 font-semibold text-xs">{cv.name}</span>
                                  {/* Fill row button */}
                                  <button
                                    onClick={() => {
                                      const val = prompt(`${cv.name} ke liye sabka stock:`);
                                      if (val && !isNaN(Number(val))) {
                                        setStockMatrix(prev => setStockValue(prev, cv.name, '__nosize__', val));
                                      }
                                    }}
                                    className="text-[9px] text-orange-500 hover:underline cursor-pointer ml-1"
                                    title={`Fill all ${cv.name}`}
                                  >
                                    fill
                                  </button>
                                </div>
                              </td>
                              {form.sizes.map(size => (
                                <td key={size} className="py-2 px-1">
                                  <input
                                    type="number"
                                    min="0"
                                    value={getStockValue(stockMatrix, cv.name, size)}
                                    onChange={e => setStockMatrix(prev => setStockValue(prev, cv.name, size, e.target.value))}
                                    placeholder="0"
                                    className="w-full px-1.5 py-1.5 rounded-lg border border-gray-200 text-xs outline-none focus:border-orange-400 text-center transition-all"
                                  />
                                </td>
                              ))}
                              <td className="py-2 px-1 text-center">
                                <span className={`font-bold text-xs ${rowTotal > 0 ? 'text-green-600' : 'text-gray-400'}`}>{rowTotal}</span>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Column totals */}
                        <tr className="border-t-2 border-gray-200">
                          <td className="py-2 pr-3 text-gray-500 font-semibold text-xs">Total</td>
                          {form.sizes.map(size => {
                            const colTotal = colorVariants.reduce((sum, cv) => sum + (Number(getStockValue(stockMatrix, cv.name, size)) || 0), 0);
                            return (
                              <td key={size} className="py-2 px-1 text-center">
                                <span className={`font-bold text-xs ${colTotal > 0 ? 'text-gray-700' : 'text-gray-300'}`}>{colTotal}</span>
                              </td>
                            );
                          })}
                          <td className="py-2 px-1 text-center">
                            <span className="font-bold text-sm text-gray-900">{stockTotal}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── CASE 2: Colors only (no sizes) ── */}
              {hasColors && !hasSizes && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <i className="ri-flashlight-line text-gray-400 text-sm shrink-0"></i>
                    <span className="text-gray-600 text-xs font-semibold whitespace-nowrap">Sabko ek saath:</span>
                    <input
                      type="number"
                      min="0"
                      value={fillAllValue}
                      onChange={e => setFillAllValue(e.target.value)}
                      placeholder="qty"
                      className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center"
                    />
                    <button
                      onClick={() => {
                        if (!fillAllValue) return;
                        let updated = { ...stockMatrix };
                        colorVariants.forEach(cv => {
                          updated = setStockValue(updated, cv.name, '__nosize__', fillAllValue);
                        });
                        setStockMatrix(updated);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-colors"
                    >
                      Fill All
                    </button>
                  </div>
                  {colorVariants.map(cv => (
                    <div key={cv.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                      <span className="w-8 h-8 rounded-full border-2 border-gray-200 shrink-0" style={{ backgroundColor: cv.hex }}></span>
                      <span className="text-gray-800 font-semibold text-sm flex-1">{cv.name}</span>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5 flex-wrap">
                          {STOCK_PRESETS.map(p => (
                            <button
                              key={p}
                              onClick={() => setStockMatrix(prev => setStockValue(prev, cv.name, '__nosize__', String(p)))}
                              className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer whitespace-nowrap ${
                                getStockValue(stockMatrix, cv.name, '__nosize__') === String(p)
                                  ? 'bg-gray-900 text-white border-gray-900'
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={getStockValue(stockMatrix, cv.name, '__nosize__')}
                          onChange={e => setStockMatrix(prev => setStockValue(prev, cv.name, '__nosize__', e.target.value))}
                          placeholder="0"
                          className="w-16 px-2 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── CASE 3: Sizes only (no colors) ── */}
              {!hasColors && hasSizes && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <i className="ri-flashlight-line text-gray-400 text-sm shrink-0"></i>
                    <span className="text-gray-600 text-xs font-semibold whitespace-nowrap">Sabko ek saath:</span>
                    <input
                      type="number"
                      min="0"
                      value={fillAllValue}
                      onChange={e => setFillAllValue(e.target.value)}
                      placeholder="qty"
                      className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center"
                    />
                    <button
                      onClick={() => {
                        if (!fillAllValue) return;
                        const updated: SizeStockMap = {};
                        form.sizes.forEach(s => { updated[s] = fillAllValue; });
                        setSizeStock(prev => ({ ...prev, ...updated }));
                      }}
                      className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-colors"
                    >
                      Fill All
                    </button>
                  </div>
                  {form.sizes.map(size => (
                    <div key={size} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                        <span className="text-gray-700 font-bold text-sm">{size}</span>
                      </div>
                      <span className="text-gray-800 font-semibold text-sm flex-1">Size {size}</span>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5 flex-wrap">
                          {STOCK_PRESETS.map(p => (
                            <button
                              key={p}
                              onClick={() => setSizeStock(prev => ({ ...prev, [size]: String(p) }))}
                              className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer whitespace-nowrap ${
                                sizeStock[size] === String(p)
                                  ? 'bg-gray-900 text-white border-gray-900'
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={sizeStock[size] ?? ''}
                          onChange={e => setSizeStock(prev => ({ ...prev, [size]: e.target.value }))}
                          placeholder="0"
                          className="w-16 px-2 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 text-center"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── CASE 4: No colors, no sizes ── */}
              {!hasColors && !hasSizes && (
                <div>
                  <div className={`rounded-2xl border-2 p-4 transition-all ${
                    errors.stock ? 'border-red-400 bg-red-50' :
                    stockTotal === 0 ? 'border-gray-200 bg-gray-50' :
                    stockTotal <= 5 ? 'border-amber-300 bg-amber-50' :
                    stockTotal <= 20 ? 'border-yellow-300 bg-yellow-50' :
                    'border-green-300 bg-green-50'
                  }`}>
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {STOCK_PRESETS.map(preset => (
                        <button
                          key={preset}
                          onClick={() => setSizeStock({ '__nosize__': String(preset) })}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                            sizeStock['__nosize__'] === String(preset) ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {preset} pcs
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                        <i className="ri-box-3-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={sizeStock['__nosize__'] ?? ''}
                        onChange={e => setSizeStock({ '__nosize__': e.target.value })}
                        placeholder="Enter stock quantity (e.g. 50)"
                        className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-all bg-white ${
                          errors.stock ? 'border-red-400' : 'border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100'
                        }`}
                      />
                    </div>
                    <p className="text-gray-500 text-xs mt-2 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      Sizes ya colours add karne ke liye Variants tab mein jaao
                    </p>
                  </div>
                </div>
              )}

              {errors.stock && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{errors.stock}
                </p>
              )}

              {/* Stock summary bar */}
              {stockTotal > 0 && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-green-700 font-bold text-sm">Total Stock: {stockTotal} units</p>
                    <p className="text-green-600 text-xs">
                      {hasColors && hasSizes
                        ? `${colorVariants.length} colours × ${form.sizes.length} sizes`
                        : hasColors
                        ? `${colorVariants.length} colour variants`
                        : hasSizes
                        ? `${form.sizes.length} sizes`
                        : 'Single stock'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
                <i className="ri-list-check text-gray-500"></i>Listing Summary
              </p>
              <div className="flex items-start gap-3">
                {(colorVariants[0]?.images[0] || mainImages[0]) ? (
                  <img
                    src={colorVariants[0]?.images[0]?.dataUrl ?? mainImages[0]?.dataUrl}
                    alt=""
                    className="w-16 h-16 rounded-xl object-cover border border-gray-200 shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
                    <i className="ri-image-line text-gray-400 text-xl"></i>
                  </div>
                )}
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-gray-900 font-semibold text-sm truncate">{form.name || '—'}</p>
                  <p className="text-gray-500 text-xs">{form.category || '—'} {form.brand ? `· ${form.brand}` : ''}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-900 font-bold text-sm">₹{form.price || '—'}</span>
                    {discount > 0 && <span className="text-green-600 text-xs font-bold">{discount}% OFF</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                    <span>{mainImages.length + colorVariants.reduce((s, c) => s + c.images.length, 0)} image{mainImages.length + colorVariants.reduce((s, c) => s + c.images.length, 0) !== 1 ? 's' : ''}</span>
                    {form.sizes.length > 0 && <span>{form.sizes.join(', ')}</span>}
                    {colorVariants.length > 0 && (
                      <span className="flex items-center gap-1">
                        {colorVariants.map(cv => (
                          <span key={cv.id} className="w-3 h-3 rounded-full border border-gray-300 inline-block" style={{ backgroundColor: cv.hex }} title={cv.name}></span>
                        ))}
                        <span>{colorVariants.length} colour{colorVariants.length > 1 ? 's' : ''}</span>
                      </span>
                    )}
                    {stockTotal > 0 && (
                      <span className={`font-semibold ${stockStatus === 'out' ? 'text-red-500' : stockStatus === 'low' ? 'text-amber-600' : 'text-green-600'}`}>
                        {stockTotal} in stock
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setActiveSection('variants')} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Listing...</>
                ) : (
                  <><i className="ri-check-line"></i>List Product</>
                )}
              </button>
            </div>
          </div>
        )}

        </>)}
      </div>
    </div>
  );
}
